import { Plus } from 'lucide-react';
import { type ReactNode, useMemo, useRef } from 'react';

import { CenterFlex, Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import AMP_KEYS from '@/configs/amp-track-keys';
import useColor from '@/hooks/domain/useColor';
import useFixed from '@/hooks/pure/useFixed';
import useStates from '@/hooks/pure/useStates';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import { filterMap } from '@/utils/lazy';
import { track } from '@/utils/track/track';

import FilterItem from './FilterItem/FilterItem';
import FilterItemSelect from './FilterItem/FilterItemSelect';
import FilterListSelect from './FilterListSelect';

type Props = {
  name?: string;
  loading?: boolean;
  tableName: string;
  filterOptions: FilterOptionT[];
  currFilterSetQuery: FilterSetQuery;
  onFilterChange: (filterQueryFormat: FilterQueryFormatT) => void;
  resetFilter: () => void;
  prefixNode?: ReactNode;
  suffixNode?: ReactNode;
  reverse?: boolean;
  hideButton?: boolean;
};

const DataFilter = (props: Props) => {
  const name = typeof props.name === 'undefined' ? 'Filter' : props.name;
  const color = useColor();
  type State = {
    filterListDropdownStatus: boolean;
    filterDropdownState: FilterOptionT | null;
  };
  const { state, setState, setValue } = useStates<State>({
    filterListDropdownStatus: false,
    filterDropdownState: null,
  });

  const filters = useMemo(() => {
    return props.currFilterSetQuery.filterQueryFormats;
  }, [props.currFilterSetQuery.filterQueryFormats]);

  const openFilterButtonRef = useRef(null);

  const filterListPos = useFixed(openFilterButtonRef, {
    offset: {
      y: -2,
    },
  });

  const filteringList = useMemo(() => {
    const newListMap = new Map<string, FilterOptionT>();
    props.filterOptions.forEach((option) => {
      newListMap.set(option.columnKey, option);
      if (option.children) {
        option.children.forEach((childOption) => {
          newListMap.set(childOption.columnKey, {
            ...childOption,
            columnName: `${option.columnName} : ${childOption.columnName}`,
          });
        });
      }
    });

    return filterMap(filters, (filter) => {
      const col = newListMap.get(filter.columnKey);
      if (!col) return null;
      return {
        ...col,
        filter,
      };
    });
  }, [filters, props.filterOptions]);

  const wholeFilterList = useMemo(
    () => props.filterOptions.filter((col) => col.filterable),
    [props.filterOptions],
  );

  const openFilterList = () => {
    track(AMP_KEYS.DATA_FILTER_CLICK, {
      tableName: props.tableName,
    });
    setValue('filterListDropdownStatus', true);
  };

  const onFilterClick = (filter: FilterOptionT) => {
    track(AMP_KEYS.DATA_FILTER_PROPERTY_SELECT, {
      tableName: props.tableName,
      property: filter.columnName,
    });
    setState((prev) => ({
      ...prev,
      filterListDropdownStatus: false,
      filterDropdownState: filter,
    }));
  };

  const currFormat = useMemo(() => {
    if (!state.filterDropdownState?.columnKey) return null;
    const format = props.currFilterSetQuery.filterQueryFormats.find(
      (format) => format.columnKey === state.filterDropdownState?.columnKey,
    );
    if (format) {
      return format;
    }
    return {
      columnKey: state.filterDropdownState?.columnKey,
      filterValues: [],
      operator: FILTER_OPERATOR.OR,
    };
  }, [
    props.currFilterSetQuery.filterQueryFormats,
    state.filterDropdownState?.columnKey,
  ]);

  if (wholeFilterList.length === 0) return null;

  return (
    <Flex g="10px" a="start" d={props.reverse ? 'row-reverse' : 'row'} f={1}>
      {!props.hideButton && (
        <Button
          leftIcon={<Plus />}
          variant="outlined"
          sizeType="m"
          onClick={openFilterList}
          ref={openFilterButtonRef}
          rounded
        >
          {name}
        </Button>
      )}

      <Flex
        a="center"
        g="6px"
        w="wrap"
        f={1}
        d={props.reverse ? 'row-reverse' : 'row'}
      >
        {props.prefixNode}
        {filterMap(filteringList, (option) => {
          const CustomFilterItem = option.FilterItem;
          if (option.children) {
            return option.children.map((childOption) => {
              if (CustomFilterItem) {
                return (
                  <CustomFilterItem
                    key={childOption.columnKey}
                    tableName={props.tableName}
                    filterOption={childOption}
                    currFilterQueryFormat={option.filter}
                    onFilterChange={props.onFilterChange}
                  />
                );
              } else {
                return (
                  <FilterItem
                    key={childOption.columnKey}
                    tableName={props.tableName}
                    filterOption={childOption}
                    currFilterQueryFormat={option.filter}
                    onFilterChange={props.onFilterChange}
                  />
                );
              }
            });
          } else if (CustomFilterItem) {
            return (
              <CustomFilterItem
                key={option.columnKey}
                tableName={props.tableName}
                filterOption={option}
                currFilterQueryFormat={option.filter}
                onFilterChange={props.onFilterChange}
              />
            );
          } else {
            return (
              <FilterItem
                key={option.columnKey}
                tableName={props.tableName}
                filterOption={option}
                currFilterQueryFormat={option.filter}
                onFilterChange={props.onFilterChange}
              />
            );
          }
        })}
        {props.suffixNode}
        {filteringList.length > 0 && !props.hideButton && (
          <Button
            variant="ghost"
            sizeType="s"
            style={{ color: color.gray400 }}
            onClick={props.resetFilter}
          >
            Clear
          </Button>
        )}
      </Flex>
      <FilterListSelect
        status={!!state.filterListDropdownStatus}
        filterList={wholeFilterList}
        pos={filterListPos}
        onFilterClick={onFilterClick}
        closeDropdown={() => setValue('filterListDropdownStatus', false)}
      />

      {state.filterDropdownState && currFormat && (
        <FilterItemSelect
          status={!!state.filterDropdownState}
          filterOption={state.filterDropdownState}
          currFilterQueryFormat={currFormat}
          onChange={props.onFilterChange}
          close={() => setValue('filterDropdownState', null)}
          triggerEl={openFilterButtonRef.current}
          modalOptions={{
            offset: {
              y: 8,
            },
          }}
        />
      )}

      <CenterFlex style={{ height: 32 }}>
        <Spinner status={!!props.loading} />
      </CenterFlex>
    </Flex>
  );
};

export default DataFilter;
